import { useMemo, useState, useCallback, useRef } from "react";
import { useSnackbar } from "./useSnackbar";
import { MbscCalendarColor, MbscEventCreateEvent, MbscEventDragEvent, MbscEventUpdateEvent, MbscEventConnection } from "@mobiscroll/react";
import { CustomMbscCalendarEventData, filterToDraggable } from "~/utils/eventsHelper";
import { CommandIndexProvidersQuery } from "~/graphql/__generated__/graphql";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { uniq } from "lodash";
import { ValidationError } from "~/utils/visitValidationUtils";
import {
  VisitDragDropData,
  createVisitDragDropData,
  validateVisitDragDrop,
  getEligibleProviderTypes,
  getLocalVisitRequest,
  getLocalVisitResource,
  isMultiResource,
  initializeFirstAssignment,
  assignVisitToState,
  getProviderId,
  invalidDropBlockers,
  applyPendingPosition,
  PendingEventPosition,
} from "./visitDragDropUtils";
import useVisitDragDrop from "./useVisitDragDrop";
import { LocalVisit, LocalVisitResource } from "./visitDragDropReducer";
import { Permission, useHasPermission, useOrganizationalUnitSegmentationEnabled } from "~/hooks/permissions";
import { useCurrentOrg } from "~/providers/GraphqlProvider";
import { createVisitEventFromResource } from "~/utils/eventsHelper";
import { filterCalendarBlocks, filterShifts, FilterState, filterVisits } from "~/views/command/filters";
import { blocksToCalendarEvents, providersToCalendarResources, shiftsToCalendarEvents, visitsToCalendarEvents } from "~/utils/mobiscrollEventFormatter";
import { UNASSIGNED_PROVIDER_ID } from "~/utils/visitsHelper";

const providerIdFromProviderCalendarResource = (resource: CustomMbscCalendarEventData["resource"]): number | null | undefined => {
  if (resource == null || resource === "" || Array.isArray(resource)) {
    return undefined;
  }

  const providerId = Number(resource);
  if (!Number.isFinite(providerId)) {
    return undefined;
  }

  return providerId === UNASSIGNED_PROVIDER_ID ? null : providerId;
};

const normalizeProviderCalendarDragEvent = (event: CustomMbscCalendarEventData): CustomMbscCalendarEventData => {
  const providerId = providerIdFromProviderCalendarResource(event.resource);

  if (providerId === undefined) {
    return event;
  }

  return {
    ...event,
    providerId,
  };
};

export const useMobiscrollProviderCalendarEvents = (
  loading: boolean,
  dragDropState: ReturnType<typeof useVisitDragDrop>,
  providerData: CommandIndexProvidersQuery["commandIndexProviders"],
  organizationalUnitMemberships: CommandIndexProvidersQuery["organizationalUnitOrgUser"],
  hasActiveOrganizationalUnits: boolean | undefined,
  lastAddedVisitId: number | null,
  filters: FilterState,
  date: Date,
  showOriginalPositions?: boolean,
) => {
  const orgConfig = useOrgConfig();
  const currentProviderId = useCurrentOrg()?.providerId ?? null;
  const hasCreateDragEventsPermission = useHasPermission(Permission.createDragEvents) ?? false;
  const organizationalUnitSegmentationEnabled = useOrganizationalUnitSegmentationEnabled();
  const snackbar = useSnackbar();

  // Drag state management
  const [medarriveEvent, setMedarriveEvent] = useState<VisitDragDropData | null>(null);
  const [shouldOpenMultiResourceAssignPopup, setShouldOpenMultiResourceAssignPopup] = useState(false);
  const [draggedEventData, setDraggedEventData] = useState<VisitDragDropData | null>(null);
  const [draggedBuffers, setDraggedBuffers] = useState<[string, string]>(["0", "0"]);

  // Collapsed groups state
  const [collapsedGroups, setCollapsedGroupsBase] = useState<Record<string, boolean>>(
    JSON.parse(localStorage?.collapsedProviderGroups || "{}")
  );
  const toggleCollapsedGroup = useCallback((typeId: number) => {
    setCollapsedGroupsBase(prev => {
      const newValue = { ...prev, [typeId]: !prev[typeId] };
      if (localStorage) { localStorage.collapsedProviderGroups = JSON.stringify(newValue); }
      return newValue;
    });
  }, []);

  // Store pending event position IMMEDIATELY when drag ends
  // This prevents snap-back by providing new position before reducer updates
  const pendingEventPositionRef = useRef<PendingEventPosition | null>(null);
  
  // Display state: applies pending position for immediate visual feedback
  const displayState = useMemo(() => {
    const { visitResources, calendarBlocks, shouldClearPending } = applyPendingPosition(
      dragDropState.localVisitResources,
      dragDropState.localCalendarBlocks,
      pendingEventPositionRef.current
    );
    if (shouldClearPending) {
      pendingEventPositionRef.current = null;
    }

    return {
      ...dragDropState,
      localVisitResources: visitResources,
      localCalendarBlocks: calendarBlocks,
    };
  }, [dragDropState]);

  const generateCareTeamProviderIds = useCallback((data: VisitDragDropData) => {
    let patient;
    let providerTypeIds: number[] = [];

    switch (data.event.type) {
      case "visit_request": {
        const localVisitRequest = getLocalVisitRequest(data);
        patient = localVisitRequest.patient;
        providerTypeIds = localVisitRequest.visitType?.visitResourceRequirements
          ?.filter((vrr) => vrr.requiresContinuityOfCare)
          ?.flatMap((vrr) => vrr.providerTypes.map(pt => pt.id)) || [];
        break;
      }
      case "visit_resource": {
        const localVisitResource = getLocalVisitResource(data);
        patient = localVisitResource.visit.patient;
        const resourceRequirement = orgConfig.visitResourceRequirements[localVisitResource.visitResourceRequirementId!];
        providerTypeIds = resourceRequirement?.requiresContinuityOfCare ?
          resourceRequirement.providerTypes.map((pt) => pt.id) :
          [];
        break;
      }
      case "calendar_block": break;
      default: (() => { throw new Error(`Unknown type: ${String(data.event.type)}`); })()
    }

    const patientCareTeam = patient?.careTeamProviders?.filter((coc) => providerTypeIds?.includes(coc.providerTypeId));

    return uniq(patientCareTeam?.map((coc) => coc.providerId));
  }, [orgConfig.visitResourceRequirements]);

  const validateAndDisplayErrors = useCallback((validationErrors: ValidationError[]) => {
    if (validationErrors.length > 0) {
      const shouldBlockDrop = validationErrors.some((error) => !error.shouldAllowDrop);
      const errorMessages = validationErrors.map((error) => error.message).join(', ');
      snackbar.setErrorMessage(errorMessages, 'warning');

      if (shouldBlockDrop) {
        return false;
      }
    }
    return true;
  }, [snackbar]);

  const isOrganizationalUnitDropAllowed = useCallback((data: VisitDragDropData, providerId: number | null) => {
    if (organizationalUnitSegmentationEnabled === false || hasActiveOrganizationalUnits === false
      || data.event.type !== 'visit_resource' || providerId == null) {
      return true;
    }

    const resource = getLocalVisitResource(data);
    if (resource.providerId === providerId) {
      return true;
    }

    const isSavedResource = dragDropState.dbVisitResources.some((dbResource) => dbResource.id === resource.id);
    if (!isSavedResource) {
      return true;
    }

    const organizationalUnitId = resource.visit.episode?.organizationalUnitId;
    if (organizationalUnitId == null) {
      return true;
    }
    return organizationalUnitMemberships?.some((membership) =>
      membership.organizationalUnitId === organizationalUnitId
      && membership.orgUser?.providers.some((provider) => provider.id === providerId)
    ) ?? false;
  }, [organizationalUnitSegmentationEnabled, hasActiveOrganizationalUnits, dragDropState.dbVisitResources, organizationalUnitMemberships]);

  const validateEventDrop = useCallback((draggedEvent: CustomMbscCalendarEventData): boolean => {
    const normalizedEvent = normalizeProviderCalendarDragEvent(draggedEvent);
    const draggableEvent = filterToDraggable(normalizedEvent);
    if (!draggableEvent) {
      return false;
    }

    const bufferBeforeMinutes = parseInt(draggedEvent.bufferBefore || "0") || 0;
    const bufferAfterMinutes = parseInt(draggedEvent.bufferAfter || "0") || 0;

    const data = createVisitDragDropData(
      draggableEvent,
      orgConfig,
      displayState.localVisitResources,
      displayState.localVisits,
      displayState.localVisitRequests,
      displayState.localShifts,
      displayState.localCalendarBlocks,
      providerData?.providers ?? [],
      bufferBeforeMinutes,
      bufferAfterMinutes,
    );

    if (!isOrganizationalUnitDropAllowed(data, draggableEvent.providerId)) {
      snackbar.setErrorMessage("Provider is not assigned to the episode's organizational unit", 'warning');
      return false;
    }

    const validationErrors = validateVisitDragDrop(data, generateCareTeamProviderIds(data));
    const shouldAllowDrop = validateAndDisplayErrors(validationErrors);

    return shouldAllowDrop;
  }, [displayState.localCalendarBlocks, displayState.localShifts, displayState.localVisitRequests, displayState.localVisitResources, displayState.localVisits, isOrganizationalUnitDropAllowed, generateCareTeamProviderIds, orgConfig, providerData?.providers, snackbar, validateAndDisplayErrors]);

  const onValidateDragCreateAndUpdate = useCallback((event: MbscEventCreateEvent | MbscEventUpdateEvent) => {
    return validateEventDrop(event.event as unknown as CustomMbscCalendarEventData);
  }, [validateEventDrop]);

  // Handler to catch when Mobiscroll rejects a drop (e.g., due to invalid zone overlap with buffers)
  const onEventUpdateFailed = useCallback(() => {
    // Show a notification so the user knows why the drop was rejected
    snackbar.setErrorMessage('Visit cannot be placed here - drive time would extend outside shift boundaries', 'warning');
  }, [snackbar]);

  const onEventDragStart = useCallback((event: MbscEventDragEvent) => {
    const draggedEvent = event.event as unknown as CustomMbscCalendarEventData;
    const normalizedEvent = normalizeProviderCalendarDragEvent(draggedEvent);
    const draggableEvent = filterToDraggable(normalizedEvent);

    if (!draggableEvent) {
      setDraggedEventData(null);
      return;
    }

    const data = createVisitDragDropData(
      draggableEvent,
      orgConfig,
      displayState.localVisitResources,
      displayState.localVisits,
      displayState.localVisitRequests,
      displayState.localShifts,
      displayState.localCalendarBlocks,
      providerData?.providers ?? []
    );

    setDraggedBuffers([draggedEvent.bufferBefore || "0", draggedEvent.bufferAfter || "0"]);
    draggedEvent.bufferBefore = "0";
    draggedEvent.bufferAfter = "0";

    // This catches edge cases where quick drags don't trigger onEventDragEnd
    document.addEventListener('pointerup', () => {
      requestAnimationFrame(() => setDraggedEventData(null));
    }, { once: true });

    setDraggedEventData(data);
  }, [displayState.localCalendarBlocks, displayState.localShifts, displayState.localVisitRequests, displayState.localVisitResources, displayState.localVisits, orgConfig, providerData?.providers]);

  const onEventDragEnd = useCallback(async (event: MbscEventDragEvent) => {
    const draggedEvent = event.event as unknown as CustomMbscCalendarEventData;
    setDraggedEventData(null);

    const normalizedEvent = normalizeProviderCalendarDragEvent(draggedEvent);
    const draggableEvent = filterToDraggable(normalizedEvent);

    if (!draggableEvent) {
      return false;
    }

    draggedEvent.bufferBefore = draggedBuffers[0];
    draggedEvent.bufferAfter = draggedBuffers[1];

    const bufferBeforeMinutes = parseInt(draggedBuffers[0]) || 0;
    const bufferAfterMinutes = parseInt(draggedBuffers[1]) || 0;

    let data = createVisitDragDropData(
      draggableEvent,
      orgConfig,
      displayState.localVisitResources,
      displayState.localVisits,
      displayState.localVisitRequests,
      displayState.localShifts,
      displayState.localCalendarBlocks,
      providerData?.providers ?? [],
      bufferBeforeMinutes,
      bufferAfterMinutes
    );

    if (!validateEventDrop(draggableEvent)) {
      return false;
    }

    const careTeamProviderIds = generateCareTeamProviderIds(data);
    const validationErrors = validateVisitDragDrop(data, careTeamProviderIds);

    const shouldAllowDrop = validateAndDisplayErrors(validationErrors);
    if (!shouldAllowDrop) {
      return false;
    }

    if (data.event.type === "visit_request" && isMultiResource(data)) {
      data = initializeFirstAssignment(data);
      setMedarriveEvent(data);
      setShouldOpenMultiResourceAssignPopup(true);
      return true;
    }

    setShouldOpenMultiResourceAssignPopup(false);

    const eventId = data.event.referenceId;
    const newProviderId = getProviderId(data);

    pendingEventPositionRef.current = {
      eventId: typeof eventId === 'number' ? eventId : Number(eventId),
      startTime: data.eventStart.toISOString(),
      endTime: data.eventEnd.toISOString(),
      providerId: newProviderId,
      eventType: data.event.type === "calendar_block" ? 'calendar_block' : 'visit_resource',
    };

    // Defer dispatch to allow Mobiscroll to complete its internal drag cycle
    // This will prevent maximum update depth exceeded error on the future if anything else causes weird cascade cycles.
    requestAnimationFrame(() => {
      assignVisitToState(data, dragDropState.dispatch);
    });
  }, [displayState.localCalendarBlocks, displayState.localShifts, displayState.localVisitRequests, displayState.localVisitResources, displayState.localVisits, dragDropState.dispatch, draggedBuffers, generateCareTeamProviderIds, orgConfig, providerData?.providers, validateAndDisplayErrors, validateEventDrop]);

  const onResetDragDrop = useCallback(() => {
    setDraggedEventData(null);
    setMedarriveEvent(null);
    setShouldOpenMultiResourceAssignPopup(false);
    dragDropState.dispatch({ type: 'RESET_MODIFICATIONS' });
  }, [dragDropState]);

  // Continuity of care shift IDs
  const continuityOfCareShiftIds = useMemo(() => {
    const shiftIds = new Set<number>();
    if (!draggedEventData) { return shiftIds; }

    const allowedProviderTypes = getEligibleProviderTypes(draggedEventData);
    const continuityOfCareProviderIds = generateCareTeamProviderIds(draggedEventData);

    displayState.localShifts.forEach(shift => {
      if (shift.provider && continuityOfCareProviderIds.includes(shift.provider.id) && allowedProviderTypes?.includes(shift.provider.providerTypeId)) {
        shiftIds.add(shift.id);
      }
    });

    return shiftIds;
  }, [displayState.localShifts, draggedEventData, generateCareTeamProviderIds]);

  // Visual-only invalid zones for styling (colors prop)
  // These show where drops are not recommended but don't block drops
  // Our custom validation in onEventUpdate handles actual blocking
  // The invalidDropBlockers already returns objects with cssClass: 'invalid-drop'
  // which is styled in StyledCalendar.ts with a diagonal striped pattern
  const invalidDropColors = useMemo(() => {
    return invalidDropBlockers(
      draggedEventData,
      providerData?.providers ?? [],
      date,
      orgConfig,
      isOrganizationalUnitDropAllowed
    );
  }, [draggedEventData, providerData?.providers, date, orgConfig, isOrganizationalUnitDropAllowed]);

  // Calendar events generation
  const isOptimizeReviewMode = (displayState.originalVisitResources?.length ?? 0) > 0
    || (displayState.skippedVisitResources?.length ?? 0) > 0;

  const calendarData = useMemo(() => {
    if (loading) {
      const calendarResources = [
        {
          id: "loading",
          name: "Loading...",
          firstName: "",
          lastName: "",
          active: true,
        },
      ];
      return {
        visitEvents: [],
        shiftEvents: [],
        calendarBlockEvents: [],
        calendarResources,
        originalVisitEvents: [],
      };
    }

    const visibleVisits = filterVisits(displayState.localVisitResources, filters, currentProviderId);
    const visibleCalendarBlocks = filterCalendarBlocks(displayState.localCalendarBlocks, filters, currentProviderId);
    const visibleShifts = filterShifts(visibleVisits, displayState.localShifts, filters, currentProviderId);

    const assignedVisitResourceIds = new Set(displayState.optimizerAssignmentIds ?? []);
    const skippedVisitResourceIds = new Set(
      displayState.skippedVisitResources?.map((vr) => vr.id) ?? []
    );
    const lockedVisitResourceIds = isOptimizeReviewMode
      ? new Set(
          visibleVisits
            .filter((vr) => !assignedVisitResourceIds.has(vr.id) && !skippedVisitResourceIds.has(vr.id))
            .map((vr) => vr.id)
        )
      : new Set<number>();

    const visitEvents = visitsToCalendarEvents(
      visibleVisits,
      displayState.localShifts,
      displayState.localVisitResources,
      displayState.localVisits,
      displayState.localVisitRequests,
      displayState.localCalendarBlocks,
      dragDropState.careRequirementVisits,
      providerData?.providers ?? [],
      lastAddedVisitId,
      hasCreateDragEventsPermission,
      displayState.modifiedResourceIds,
      orgConfig,
      isOptimizeReviewMode,
      lockedVisitResourceIds
    );

    const originalVisitEvents: CustomMbscCalendarEventData[] = displayState.originalVisitResources?.map((vr) => {
      const event = createVisitEventFromResource(
        vr,
        null,
        false,
        new Set(),
        false,
        undefined,
        orgConfig,
      );
  
      return {
        ...event,
        id: `ghost-${vr.id}`,
        cssClass: `ghost-event ghost-visit-${vr.visit.id} ${showOriginalPositions ? 'visible' : ''}`,
      }
    }) ?? [];

    const calendarBlockEvents = blocksToCalendarEvents(visibleCalendarBlocks, displayState.modifiedCalendarBlocks);
    const shiftEvents = shiftsToCalendarEvents(visibleShifts);
    const calendarResources = providersToCalendarResources(providerData?.providersByType ?? [], visibleShifts, visibleVisits, visibleCalendarBlocks, date, collapsedGroups, filters, currentProviderId);

    return {
      visitEvents,
      shiftEvents,
      calendarBlockEvents,
      calendarResources,
      originalVisitEvents,
    };
  }, [
    loading,
    displayState.localVisitResources,
    displayState.localCalendarBlocks,
    displayState.localShifts,
    displayState.localVisits,
    displayState.localVisitRequests,
    displayState.modifiedResourceIds,
    displayState.originalVisitResources,
    displayState.modifiedCalendarBlocks,
    displayState.optimizerAssignmentIds,
    displayState.skippedVisitResources,
    filters,
    currentProviderId,
    providerData,
    lastAddedVisitId,
    hasCreateDragEventsPermission,
    orgConfig,
    dragDropState.careRequirementVisits,
    isOptimizeReviewMode,
    date,
    collapsedGroups,
    showOriginalPositions
  ]);

  const calendarColors: MbscCalendarColor[] = useMemo(() => {
    const filteringBlocks = calendarData.calendarBlockEvents
      .filter(e => e.type === 'calendar_block_filter')
      .filter(e => !e.cssClass?.includes('canceled-block'));
    
    const filterBlockColors: MbscCalendarColor[] = filteringBlocks.map((block) => ({
      resource: block.resource,
      background: "#F8F8F8",
      start: block.start,
      end: block.end,
    }));

    return [...filterBlockColors, ...invalidDropColors];
  }, [calendarData.calendarBlockEvents, invalidDropColors]);

  return {
    // Calendar data
    ...calendarData,
    // Drag-drop handlers
    calendarColors,
    onEventCreate: onValidateDragCreateAndUpdate,
    onEventUpdate: onValidateDragCreateAndUpdate,
    onEventUpdateFailed,
    onEventDragStart,
    onEventDragEnd,
    // Other
    onResetDragDrop,
    continuityOfCareShiftIds,
    toggleCollapsedGroup,
    medarriveEvent,
    setMedarriveEvent,
    shouldOpenMultiResourceAssignPopup,
    setShouldOpenMultiResourceAssignPopup,
  };
};

export const useConnections = (localVisits: LocalVisit[], originalVisitResources?: LocalVisitResource[]) => useMemo(() => {
  const lines: MbscEventConnection[] = [];

  for (const visit of localVisits) {
    if (visit.visitResources.length >= 2) {
      for (let i = 0; i < visit.visitResources.length - 1; i++) {
        lines.push({
          from: visit.visitResources[i].id,
          to: visit.visitResources[i + 1].id,
          color: 'gray',
          arrow: false,
          type: 'ss',
          cssClass: `dashed-connection visit-${visit.id}`,
        });
      }
    }
  }

  originalVisitResources?.forEach((vr) => {
    lines.push({
      from: `ghost-${vr.id}`,
      to: vr.id,
      color: 'black',
      arrow: true,
      type: 'fs',
      cssClass: `dashed-connection connection-arrow visit-${vr.visit.id}`,
    });
  })

  return lines;
}, [localVisits, originalVisitResources]);
